import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, Phone, Mail, MapPin, Edit2, Trash2, 
  Briefcase, Camera, FileText, DollarSign, Calendar,
  CheckCircle, Clock, AlertCircle, Send, RotateCcw,
  ChevronRight, ExternalLink, User, TrendingUp, Package
} from 'lucide-react';
import { customersApi, jobsApi, invoicesApi, customerAssetsApi } from '../services/api';
import { formatCurrency, formatDate, formatPhoneNumber, getStatusColor, getInitials } from '../utils/helpers';
import { useBusinessContext } from '../contexts/BusinessContext';
import Loading from '../components/Loading';
import Modal from '../components/Modal';
import ConfirmModal from '../components/ConfirmModal';
import MapLink from '../components/MapLink';
import JobPhotos from '../components/jobs/JobPhotos';
import JobDetailDrawer from '../components/jobs/JobDetailDrawer';
import CustomerPricingHistory from '../components/customers/CustomerPricingHistory';
import jobPhotoService from '../services/jobPhotoService';
import CustomerAssetsTab from '../components/customers/CustomerAssetsTab';
import { industryUsesAssets } from '../config/assetTemplates';
import { industryService } from '../services/industryService';

/**
 * Customer Detail Page - Shows full customer info with jobs, photos, invoices, pricing history
 */
const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useBusinessContext();
  const isOwner = user?.role === 'Owner';
  const [industryType, setIndustryType] = useState('Other');
  const showAssetsTab = industryUsesAssets(industryType);
  
  // Fetch business industry type
  useEffect(() => {
    const fetchIndustry = async () => {
      try {
        const theme = await industryService.getMyTheme();
        setIndustryType(theme?.serviceType || 'Other');
      } catch (err) {
        console.log('Could not fetch industry:', err);
      }
    };
    fetchIndustry();
  }, []);

  // Data state
  const [customer, setCustomer] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [assetCount, setAssetCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UI state
  const [activeTab, setActiveTab] = useState('jobs');
  const [selectedJob, setSelectedJob] = useState(null);
  
  // Modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    variant: 'danger',
    confirmText: 'Confirm',
  });

  // Load all data
  useEffect(() => {
    if (id) {
      loadCustomerData();
    }
  }, [id]);

  const loadCustomerData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load customer details
      const customerRes = await customersApi.get(id);
      setCustomer(customerRes.data);

      // Load customer's jobs
      const jobsRes = await jobsApi.getByCustomer(id);
      setJobs(jobsRes.data || []);

      // Load customer's invoices (owner only)
      if (isOwner) {
        try {
          const invoicesRes = await invoicesApi.getByCustomer(id);
          setInvoices(invoicesRes.data || []);
        } catch (err) {
          console.log('Could not load invoices:', err);
          setInvoices([]);
        }
      }

      // Property count for the Assets tab badge
      try {
        const assetsRes = await customerAssetsApi.getByCustomer(id);
        setAssetCount((assetsRes.data || []).length);
      } catch (err) {
        setAssetCount(0);
      }

      // Load all photos for this customer's jobs
      try {
        const photosRes = await jobPhotoService.getCustomerPhotos(id);
        setPhotos(photosRes.data || []);
      } catch (err) {
        console.log('Could not load photos:', err);
        setPhotos([]);
      }

    } catch (err) {
      console.error('Error loading customer:', err);
      setError('Customer not found');
    } finally {
      setLoading(false);
    }
  };

  // Drawer handlers
  const handleDrawerClose = () => {
    setSelectedJob(null);
  };

  const handleJobEdit = (job) => {
    setSelectedJob(null);
    navigate('/jobs', { 
      state: { 
        editJobId: job.id,
        returnTo: `/customers/${id}`
      } 
    });
  };

  const handleJobDelete = async (jobId) => {
    try {
      await jobsApi.delete(jobId);
      setSelectedJob(null);
      loadCustomerData();
    } catch (err) {
      console.error('Error deleting job:', err);
    }
  };

  const handleJobComplete = (job) => {
    setSelectedJob(null);
    navigate('/jobs', { 
      state: { 
        completeJobId: job.id,
        returnTo: `/customers/${id}`
      } 
    });
  };

  const handleViewJob = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    if (job) {
      setSelectedJob({
        ...job,
        customer: {
          id: customer.id,
          name: customer.name,
          email: customer.email,
          phone: customer.phone,
          address: customer.address,
          city: customer.city,
          state: customer.state,
          zipCode: customer.zipCode,
          billingType: customer.billingType,
          billingDay: customer.billingDay,
          contractRate: customer.contractRate
        }
      });
    }
  };

  // Calculate stats
  const stats = {
    totalJobs: jobs.length,
    completedJobs: jobs.filter(j => j.status === 'Completed').length,
    scheduledJobs: jobs.filter(j => j.status === 'Scheduled').length,
    totalRevenue: jobs
      .filter(j => j.status === 'Completed')
      .reduce((sum, j) => sum + (j.finalAmount || j.quoteAmount || 0), 0),
    totalPhotos: photos.length,
    pendingInvoices: invoices.filter(i => i.status === 'Pending' || i.status === 'Sent').length,
  };

  // Handle portal access
  const handleSendPortalAccess = async () => {
    if (!customer?.email) {
      setConfirmModal({
        isOpen: true,
        title: 'No Email Address',
        message: 'This customer does not have an email address.',
        variant: 'warning',
        confirmText: 'OK',
        onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
      });
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: 'Send Portal Access',
      message: `Send a secure portal access link to ${customer.name} at ${customer.email}?`,
      variant: 'info',
      confirmText: 'Send Email',
      onConfirm: async () => {
        try {
          await customersApi.sendPortalAccess(customer.id);
          setConfirmModal({
            isOpen: true,
            title: 'Email Sent!',
            message: `Portal access email sent to ${customer.email}.`,
            variant: 'success',
            confirmText: 'OK',
            onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
          });
        } catch (err) {
          setConfirmModal({
            isOpen: true,
            title: 'Error',
            message: 'Failed to send portal access email.',
            variant: 'danger',
            confirmText: 'OK',
            onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
          });
        }
      },
    });
  };

  // Handle delete/deactivate
  const handleDelete = () => {
    if (!isOwner) return;

    const hasHistory = stats.totalJobs > 0 || invoices.length > 0;

    if (hasHistory) {
      setConfirmModal({
        isOpen: true,
        title: 'Deactivate Customer',
        message: `"${customer.name}" has ${stats.totalJobs} job(s) and cannot be deleted. Would you like to deactivate them instead?`,
        variant: 'warning',
        confirmText: 'Deactivate',
        onConfirm: async () => {
          try {
            await customersApi.deactivate(customer.id);
            navigate('/customers');
          } catch (err) {
            console.error('Error deactivating:', err);
          }
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        },
      });
    } else {
      setConfirmModal({
        isOpen: true,
        title: 'Delete Customer',
        message: `Are you sure you want to delete "${customer.name}"? This cannot be undone.`,
        variant: 'danger',
        confirmText: 'Delete',
        onConfirm: async () => {
          try {
            await customersApi.delete(customer.id);
            navigate('/customers');
          } catch (err) {
            console.error('Error deleting:', err);
          }
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        },
      });
    }
  };

  // Handle reactivate
  const handleReactivate = async () => {
    if (!isOwner) return;
    try {
      await customersApi.reactivate(customer.id);
      loadCustomerData();
    } catch (err) {
      console.error('Error reactivating:', err);
    }
  };

  if (loading) return <Loading fullScreen />;

  if (error || !customer) {
    return (
      <div className="p-4 lg:p-8">
        <div className="text-center py-12">
          <User className="mx-auto h-12 w-12 text-gray-300 mb-4" />
          <p className="text-gray-500 text-lg font-medium mb-2">Customer not found</p>
          <Link to="/customers" className="text-primary-600 hover:underline">
            ← Back to Customers
          </Link>
        </div>
      </div>
    );
  }

  // Build tabs array based on permissions and industry
  const tabs = [
    { id: 'jobs', label: 'Jobs', icon: Briefcase, count: stats.totalJobs },
    { id: 'photos', label: 'Photos', icon: Camera, count: stats.totalPhotos },
    ...(showAssetsTab ? [{ id: 'assets', label: 'Assets', icon: Package, count: assetCount }] : []),
    ...(isOwner ? [{ id: 'pricing', label: 'Pricing', icon: TrendingUp, count: null }] : []),
    ...(isOwner ? [{ id: 'invoices', label: 'Invoices', icon: FileText, count: invoices.length }] : []),
  ];

  return (
    <div className="p-4 lg:p-8 pb-24 lg:pb-8">
      {/* Back Button */}
      <Link 
        to="/customers" 
        className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-4"
      >
        <ArrowLeft size={16} />
        Back to Customers
      </Link>

      {/* Customer Header Card */}
      <div className="card p-0 overflow-hidden mb-6">
        {/* Inactive Banner */}
        {customer.isActive === false && (
          <div className="bg-gray-100 border-b border-gray-200 px-4 py-2 flex items-center justify-between">
            <span className="text-sm text-gray-600 font-medium">This customer is inactive</span>
            {isOwner && (
              <button
                onClick={handleReactivate}
                className="text-sm text-green-600 hover:text-green-700 font-medium flex items-center gap-1"
              >
                <RotateCcw size={14} />
                Reactivate
              </button>
            )}
          </div>
        )}

        <div className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-start gap-4">
            {/* Avatar */}
            <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center font-bold text-lg sm:text-xl flex-shrink-0 ${
              customer.isActive === false 
                ? 'bg-gray-200 text-gray-500' 
                : 'bg-primary-100 text-primary-700'
            }`}>
              {getInitials(customer.name)}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{customer.name}</h1>
                {customer.isActive === false && (
                  <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs font-medium rounded-full">
                    Inactive
                  </span>
                )}
              </div>

              {/* Contact Info */}
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-1 sm:gap-x-4 sm:gap-y-1 text-sm text-gray-600 mt-2">
                {customer.email && (
                  <a href={`mailto:${customer.email}`} className="flex items-center gap-1.5 hover:text-primary-600 truncate">
                    <Mail size={14} className="flex-shrink-0" />
                    <span className="truncate">{customer.email}</span>
                  </a>
                )}
                {customer.phone && (
                  <a href={`tel:${customer.phone}`} className="flex items-center gap-1.5 hover:text-primary-600">
                    <Phone size={14} className="flex-shrink-0" />
                    {formatPhoneNumber(customer.phone)}
                  </a>
                )}
              </div>

              {/* Address */}
              {customer.address && (
                <div className="mt-2">
                  <MapLink
                    address={customer.address}
                    city={customer.city}
                    state={customer.state}
                    zipCode={customer.zipCode}
                    className="text-sm"
                  />
                </div>
              )}

              {/* Notes */}
              {customer.notes && (
                <p className="text-sm text-gray-500 mt-2 italic">"{customer.notes}"</p>
              )}
            </div>
          </div>

          {/* Contract Info */}
          {customer.billingType && customer.billingType !== 'PerJob' && (
            <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-100">
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                <span className="text-purple-700">
                  <strong>Billing:</strong> {customer.billingType} on day {customer.billingDay || '1'}
                </span>
                {customer.contractRate && (
                  <span className="text-purple-700">
                    <strong>Rate:</strong> {formatCurrency(customer.contractRate)}/{customer.billingType === 'Weekly' ? 'week' : customer.billingType === 'Monthly' ? 'month' : 'quarter'}
                  </span>
                )}
              </div>
              {customer.contractNotes && (
                <p className="text-xs text-purple-600 mt-1">{customer.contractNotes}</p>
              )}
            </div>
          )}

          {/* Action Buttons - Horizontal scroll on mobile */}
          <div className="flex gap-2 mt-4 overflow-x-auto pb-1 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap">
            {customer.email && (
              <button
                onClick={handleSendPortalAccess}
                className="flex-shrink-0 px-3 py-2 text-sm font-medium border border-blue-200 text-blue-600 hover:bg-blue-50 rounded-lg flex items-center gap-1.5 whitespace-nowrap"
              >
                <Send size={14} />
                Portal Access
              </button>
            )}

            <button
              onClick={() => navigate('/customers', { state: { editCustomerId: customer.id } })}
              className="flex-shrink-0 px-3 py-2 text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50 rounded-lg flex items-center gap-1.5 whitespace-nowrap"
            >
              <Edit2 size={14} />
              Edit
            </button>

            {isOwner && customer.isActive !== false && (
              <button
                onClick={handleDelete}
                className="flex-shrink-0 px-3 py-2 text-sm font-medium border border-red-200 text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-1.5 whitespace-nowrap"
              >
                <Trash2 size={14} />
                {stats.totalJobs > 0 ? 'Deactivate' : 'Delete'}
              </button>
            )}
          </div>
        </div>

        {/* Stats Row */}
        <div className="border-t border-gray-200 bg-gray-50 px-4 sm:px-6 py-3">
          <div className="flex flex-wrap gap-x-4 sm:gap-x-6 gap-y-2">
            <div className="flex items-center gap-1.5">
              <Briefcase size={14} className="text-gray-400" />
              <span className="text-sm">
                <strong className="text-gray-900">{stats.totalJobs}</strong>
                <span className="text-gray-500 ml-1">jobs</span>
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircle size={14} className="text-green-500" />
              <span className="text-sm">
                <strong className="text-gray-900">{stats.completedJobs}</strong>
                <span className="text-gray-500 ml-1">completed</span>
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <Camera size={14} className="text-gray-400" />
              <span className="text-sm">
                <strong className="text-gray-900">{stats.totalPhotos}</strong>
                <span className="text-gray-500 ml-1">photos</span>
              </span>
            </div>
            {isOwner && (
              <div className="flex items-center gap-1.5">
                <DollarSign size={14} className="text-green-500" />
                <span className="text-sm">
                  <strong className="text-gray-900">{formatCurrency(stats.totalRevenue)}</strong>
                  <span className="text-gray-500 ml-1">revenue</span>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs - Responsive */}
      <div className="bg-white rounded-xl border border-gray-200 p-1.5 mb-4">
        <div className="flex">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex-1 relative flex items-center justify-center gap-1.5 py-2.5 px-2 rounded-lg transition-all
                  ${isActive 
                    ? 'bg-primary-50 text-primary-700' 
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }
                `}
              >
                {/* Icon with optional badge */}
                <div className="relative">
                  <Icon size={20} />
                  {/* Badge - Mobile only (count > 0) */}
                  {tab.count > 0 && (
                    <span className={`
                      sm:hidden absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 
                      flex items-center justify-center text-[10px] font-bold rounded-full
                      ${isActive ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'}
                    `}>
                      {tab.count > 99 ? '99+' : tab.count}
                    </span>
                  )}
                </div>
                
                {/* Label - Desktop only */}
                <span className="hidden sm:inline text-sm font-medium">
                  {tab.label}
                </span>
                
                {/* Count - Desktop only */}
                {tab.count > 0 && (
                  <span className={`
                    hidden sm:inline text-xs px-1.5 py-0.5 rounded-full
                    ${isActive ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-500'}
                  `}>
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="min-h-[300px]">
        {/* Jobs Tab */}
        {activeTab === 'jobs' && (
          <div>
            {jobs.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
                <Briefcase className="mx-auto h-10 w-10 text-gray-300 mb-3" />
                <p className="text-gray-500 font-medium">No jobs yet</p>
                <button
                  onClick={() => navigate('/jobs', { state: { createJobForCustomer: customer.id } })}
                  className="mt-3 text-sm text-primary-600 hover:underline"
                >
                  Create first job →
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                {/* Simple list header - Desktop only */}
                <div className="hidden sm:grid sm:grid-cols-12 gap-4 px-4 py-2.5 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  <div className="col-span-2">Status</div>
                  <div className="col-span-3">Date</div>
                  <div className="col-span-4">Address</div>
                  <div className="col-span-3 text-right">Amount</div>
                </div>
                
                <div className="divide-y divide-gray-100">
                  {jobs.map((job) => (
                    <button
                      key={job.id}
                      onClick={() => setSelectedJob({
                        ...job,
                        customer: {
                          id: customer.id,
                          name: customer.name,
                          email: customer.email,
                          phone: customer.phone,
                          address: customer.address,
                          city: customer.city,
                          state: customer.state,
                          zipCode: customer.zipCode,
                          billingType: customer.billingType,
                          billingDay: customer.billingDay,
                          contractRate: customer.contractRate
                        }
                      })}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
                    >
                      {/* Mobile layout */}
                      <div className="sm:hidden">
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getStatusColor(job.status)}`}>
                            {job.status}
                          </span>
                          {isOwner && (job.quoteAmount > 0 || job.finalAmount > 0) && (
                            <span className="font-semibold text-gray-900">
                              {formatCurrency(job.finalAmount || job.quoteAmount)}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-900 truncate">{job.address}</p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                          {job.scheduledDate && (
                            <span>{formatDate(job.scheduledDate)}</span>
                          )}
                          {job.jobServices?.length > 0 && (
                            <>
                              <span>•</span>
                              <span className="truncate">
                                {job.jobServices.map(js => js.serviceTypeName).filter(Boolean).join(', ')}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      
                      {/* Desktop layout */}
                      <div className="hidden sm:grid sm:grid-cols-12 gap-4 items-center">
                        <div className="col-span-2">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getStatusColor(job.status)}`}>
                            {job.status}
                          </span>
                        </div>
                        <div className="col-span-3 text-sm text-gray-600">
                          {job.scheduledDate ? formatDate(job.scheduledDate) : 
                           job.completedDate ? formatDate(job.completedDate) : '—'}
                        </div>
                        <div className="col-span-4">
                          <p className="text-sm text-gray-900 truncate">{job.address}</p>
                          {job.jobServices?.length > 0 && (
                            <p className="text-xs text-gray-500 truncate">
                              {job.jobServices.map(js => js.serviceTypeName).filter(Boolean).join(', ')}
                            </p>
                          )}
                        </div>
                        <div className="col-span-3 text-right">
                          {isOwner && (job.quoteAmount > 0 || job.finalAmount > 0) && (
                            <span className="font-semibold text-gray-900">
                              {formatCurrency(job.finalAmount || job.quoteAmount)}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Photos Tab */}
        {activeTab === 'photos' && (
          <div>
            {photos.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
                <Camera className="mx-auto h-10 w-10 text-gray-300 mb-3" />
                <p className="text-gray-500 font-medium">No photos yet</p>
                <p className="text-sm text-gray-400 mt-1">
                  Photos added to jobs will appear here
                </p>
              </div>
            ) : (
              <div>
                <p className="text-sm text-gray-500 mb-4">
                  {photos.length} photo{photos.length !== 1 ? 's' : ''} across all jobs
                </p>
                
                {/* Group photos by job */}
                {(() => {
                  const photosByJob = photos.reduce((acc, photo) => {
                    const jobId = photo.jobId;
                    if (!acc[jobId]) {
                      acc[jobId] = {
                        jobId,
                        jobAddress: photo.jobAddress || 'Unknown',
                        jobDate: photo.jobScheduledDate,
                        photos: []
                      };
                    }
                    acc[jobId].photos.push(photo);
                    return acc;
                  }, {});

                  return Object.values(photosByJob).map((group) => (
                    <div key={group.jobId} className="mb-6">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{group.jobAddress}</p>
                          {group.jobDate && (
                            <p className="text-xs text-gray-500">{formatDate(group.jobDate)}</p>
                          )}
                        </div>
                        <button
                          onClick={() => handleViewJob(group.jobId)}
                          className="text-xs text-primary-600 hover:underline"
                        >
                          View Job →
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                        {group.photos.map((photo) => (
                          <div
                            key={photo.id}
                            className="aspect-square rounded-lg overflow-hidden bg-gray-100 relative group"
                          >
                            <img
                              src={photo.thumbnailUrl || photo.url}
                              alt={photo.caption || ''}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                            <div className={`absolute top-1 left-1 px-1.5 py-0.5 rounded text-xs font-medium ${
                              photo.type === 'Before' ? 'bg-blue-100 text-blue-700' :
                              photo.type === 'After' ? 'bg-green-100 text-green-700' :
                              photo.type === 'Issue' ? 'bg-amber-100 text-amber-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {photo.type}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}
          </div>
        )}

        {/* Pricing History Tab - Owner Only */}
        {activeTab === 'pricing' && isOwner && (
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <CustomerPricingHistory 
              customerId={parseInt(id)}
              onViewJob={handleViewJob}
            />
          </div>
        )}

        {/* Assets Tab */}
        {activeTab === 'assets' && showAssetsTab && (
          <CustomerAssetsTab 
            customerId={parseInt(id)}
            customerName={customer.name}
            onCountChange={setAssetCount}
          />
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && isOwner && (
          <div>
            {invoices.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
                <FileText className="mx-auto h-10 w-10 text-gray-300 mb-3" />
                <p className="text-gray-500 font-medium">No invoices yet</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                {/* Header - Desktop only */}
                <div className="hidden sm:grid sm:grid-cols-12 gap-4 px-4 py-2.5 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  <div className="col-span-4">Invoice</div>
                  <div className="col-span-3">Date</div>
                  <div className="col-span-3 text-right">Amount</div>
                  <div className="col-span-2 text-right">Status</div>
                </div>
                
                <div className="divide-y divide-gray-100">
                  {invoices.map((invoice) => (
                    <div
                      key={invoice.id}
                      className="px-4 py-3"
                    >
                      {/* Mobile layout */}
                      <div className="sm:hidden">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-medium text-gray-900">
                            #{invoice.invoiceNumber}
                          </p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            invoice.status === 'Paid' ? 'bg-green-100 text-green-700' :
                            invoice.status === 'Sent' ? 'bg-blue-100 text-blue-700' :
                            invoice.status === 'Overdue' ? 'bg-red-100 text-red-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {invoice.status}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-500">{formatDate(invoice.invoiceDate)}</span>
                          <span className="font-semibold text-gray-900">
                            {formatCurrency(invoice.totalAmount)}
                          </span>
                        </div>
                      </div>
                      
                      {/* Desktop layout */}
                      <div className="hidden sm:grid sm:grid-cols-12 gap-4 items-center">
                        <div className="col-span-4">
                          <p className="font-medium text-gray-900">#{invoice.invoiceNumber}</p>
                        </div>
                        <div className="col-span-3 text-sm text-gray-500">
                          {formatDate(invoice.invoiceDate)}
                        </div>
                        <div className="col-span-3 text-right font-semibold text-gray-900">
                          {formatCurrency(invoice.totalAmount)}
                        </div>
                        <div className="col-span-2 text-right">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            invoice.status === 'Paid' ? 'bg-green-100 text-green-700' :
                            invoice.status === 'Sent' ? 'bg-blue-100 text-blue-700' :
                            invoice.status === 'Overdue' ? 'bg-red-100 text-red-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {invoice.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Job Detail Drawer */}
      {selectedJob && (
        <JobDetailDrawer
          job={selectedJob}
          isOpen={!!selectedJob}
          onClose={handleDrawerClose}
          onEdit={handleJobEdit}
          onDelete={handleJobDelete}
          onComplete={handleJobComplete}
          onSendPrepEmail={() => {}}
          onSendQuoteEmail={() => {}}
          sendingEmail={null}
          sendingQuoteEmail={null}
        />
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        confirmText={confirmModal.confirmText}
      />
    </div>
  );
};

export default CustomerDetail;